import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../../contexts/AuthContext'
import { useFetch } from '../useFetch'

import { CONFIG } from '../../config'
import { CourtModel } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { SliceResponse, SortDirectionType, SortType } from '../../types/listing'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'

const buildQueryParams = (
  params: Record<string, string | string[] | number | undefined>
) =>
  Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map(
            item =>
              `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
          )
        : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join('&')

export function useCourts() {
  const { t } = useTranslation('exceptions')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)

  const resetState = () => {
    setLoading(false)
    setError(null)
  }

  const courts = async (
    size?: number,
    page?: number,
    sort?: SortType,
    search?: string,
    direction?: SortDirectionType
  ) => {
    setLoading(true)
    const queryParams = buildQueryParams({
      size: size ?? 10,
      page: page ?? 0,
      search: search ?? '',
      sort: sort ?? SortType.DATE,
      direction: direction ?? SortDirectionType.ASC,
    })
    try {
      const data = await fetcher<SliceResponse<CourtModel>>(
        `${CONFIG.BOUNCE_API}/api/v1/courts?${queryParams}`
      )
      if (data) {
        setError(null)
        return data
      }
    } catch (error) {
      console.log(error)
      if (error instanceof ApiError) {
        const translatedMessage = getErrorMessage(error.data)
        setError(translatedMessage)
      } else setError(t('exceptions:unknown'))
    } finally {
      setLoading(false)
    }
  }

  return { courts, loading, error, resetState }
}
